// components/integrations/IntegrationDataMappingDiagram.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactFlow, {
  addEdge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Position,
} from "reactflow";

import "reactflow/dist/style.css";
import CustomNode from "@components/misc/CustomNode";
import RootNode from "@components/misc/RootNode";

// Import the utility functions
import DataTranslationButtonNode from "@components/misc/DataTranslationButtonNode";
import DataTranslateNode from "@components/misc/DataTranslateNode";
import DataTranslationForm from "@components/misc/DataTranslationForm";

import { createNodesAndEdgesFromSchema, isValidConnection } from "@utils/dataMappingUtils";
import { extractPlaceholders, getDynamicSchema } from "@utils/utils";
import { v4 as uuidv4 } from "uuid";

import { useSelector, useDispatch } from "react-redux"; // Import useSelector and useDispatch
import { updateIntegrationMappingDetails } from "@redux/slices/integrationSlice";
import { toast } from "react-toastify";



import { parse, Kind } from "graphql";

const getUniqueTranslationId = () => {
  return uuidv4();
}

const nodeTypes = {
  custom: CustomNode,
  root: RootNode,
  buttonNode: DataTranslationButtonNode,
  translateNode: DataTranslateNode,
};

const DataMappingDiagram = ({ interactive, onMappingsAdded }) => {
  const reactFlowWrapper = useRef(null);
  const { fitView, setCenter, getEdges, getNodes } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [showSidebar, setShowSidebar] = useState(false);
  const [edgeTooltip, setEdgeTooltip] = useState(null); // { x, y } or null
  const [selectedNode, setSelectedNode] = useState(null);
  const reactFlowInstance = useRef(null);
  const [triggerRequestSchema, setTriggerRequestSchema] = useState(null);
  const [sourceRequestSchema, setSourceRequestSchema] = useState(null);
  const [sourceRequestUrl, setSourceRequestUrl] = useState("")
  // eslint-disable-next-line no-unused-vars
  const [, setSourceRequestHeaders] = useState([]);
  const [sourceResponseSchema, setSourceResponseSchema] = useState(null);
  const [destinationRequestSchema, setDestinationRequestSchema] = useState(null);
  const [destinationResponseSchema, setDestinationResponseSchema] = useState(null);
  const [triggerResponseSchema, setTriggerResponseSchema] = useState(null);
  const [dataMappings, setDataMappings] = useState([]);
  const sourceRootNodeX = 800;
  const sourceRequestNodeXOffset = 150;
  const nodeYStart = 10;
  const nodeYIncrement = 60;

  const dispatch = useDispatch();
  const integrationDetails = useSelector((state) => state.integration.selectedIntegration);
  // eslint-disable-next-line no-unused-vars
  const status = useSelector((state) => state.integration.status);
  // eslint-disable-next-line no-unused-vars
  const error = useSelector((state) => state.integration.error);



  // eslint-disable-next-line no-unused-vars
  function generateJsonSchemaFromGraphQLQuery(query) {
    try {
      const parsedQuery = parse(query);
      const operationDefinition = parsedQuery.definitions.find(
        (def) => def.kind === Kind.OPERATION_DEFINITION
      );

      if (!operationDefinition) {
        console.error("No operation definition found in the query.");
        return null;
      }

      const fields = {};
      const required = [];

      const selectionSet = operationDefinition.selectionSet;
      if (selectionSet && selectionSet.selections) {
        selectionSet.selections.forEach((selection) => {
          if (selection.kind === Kind.FIELD) {
            const fieldName = selection.name.value;
            if (selection.selectionSet && selection.selectionSet.selections) {
              // Handle nested fields recursively
              const { nestedFields, nestedRequired } = processNestedFields(selection.selectionSet.selections);
              fields[fieldName] = {
                type: "object",
                properties: nestedFields,
                required: nestedRequired,
              };
            } else {
              // Handle simple fields
              fields[fieldName] = { type: "string" }; // Default type is string
              required.push(fieldName);
            }
          }
        });
      }

      return {
        type: "object",
        properties: fields,
        required: required,
      };
    } catch (error) {
      console.error("Error parsing GraphQL query:", error);
      return null;
    }
  }

  function processNestedFields(selections) {
    const nestedFields = {};
    const nestedRequired = [];

    selections.forEach((nestedSelection) => {
      if (nestedSelection.kind === Kind.FIELD) {
        const nestedFieldName = nestedSelection.name.value;
        if (nestedSelection.selectionSet && nestedSelection.selectionSet.selections) {
          // Recursively handle deeper nested fields
          const { nestedFields: deeperNestedFields, nestedRequired: deeperNestedRequired } = processNestedFields(nestedSelection.selectionSet.selections);
          nestedFields[nestedFieldName] = {
            type: "object",
            properties: deeperNestedFields,
            required: deeperNestedRequired,
          };
        } else {
          nestedFields[nestedFieldName] = { type: "string" }; // Default type is string
          nestedRequired.push(nestedFieldName);
        }
      }
    });

    return { nestedFields, nestedRequired };
  }




  // const extractGraphQLVariables = (query) => {
  //   const variableRegex = /\$\w+:\s*\w+/g;
  //   const variables = {};
  //   let match;

  //   while ((match = variableRegex.exec(query)) !== null) {
  //     const parts = match[0].substring(1).split(':');
  //     const name = parts[0].trim();
  //     const type = parts[1].trim();

  //     variables[name] = {
  //       type: type.toLowerCase(), // Convert to lowercase for JSON schema consistency
  //     };
  //   }

  //   return {
  //     type: "object",
  //     properties: variables,
  //     required: Object.keys(variables),
  //   };
  // }

  // const graphqlQuery = `
  // query GetProducts($priceGreaterThan: Float, $category: String, $searchString: String) {
  //   products(where: {
  //     price_gt: $priceGreaterThan, # Price greater than a given value
  //     category: $category,       # Filter by category
  //     name_contains: $searchString # Filter by name containing a string
  //   }) {
  //     id
  //     name
  //     price
  //     category
  //     description
  //   }
  // }
  // `;


  // function generateJsonSchemaFromFields(fieldsString) {
  //   const fields = fieldsString
  //     .split('\n')
  //     .map((field) => field.trim())
  //     .filter((field) => field !== '');

  //   const properties = {};
  //   const required = [];

  //   fields.forEach((field) => {
  //     if (field.includes('{') && field.includes('}')) {
  //       // Handle nested object
  //       const fieldName = field.split('{')[0].trim();
  //       const innerFieldsString = field
  //         .substring(field.indexOf('{') + 1, field.lastIndexOf('}'))
  //         .trim();
  //       const innerFields = generateJsonSchemaFromFields(innerFieldsString); // Recursive call

  //       properties[fieldName] = {
  //         type: 'object',
  //         properties: innerFields.properties,
  //         required: innerFields.required,
  //       };
  //     } else if (!field.includes('{') && !field.includes('}')) {
  //       // Handle simple field
  //       properties[field] = { type: 'string' };
  //     }
  //     if(!field.includes('{')){
  //       required.push(field.split('{')[0].trim());
  //     }
  //   });

  //   if (properties.price && properties.price.properties && properties.price.properties.value) {
  //     properties.price.properties.value.type = "number"
  //   }

  //   const schema = {
  //     type: 'object',
  //     properties: properties,
  //     required: required.map(field => field.split('{')[0].trim()), // Ensure required array uses base field names
  //   };

  //   return schema;
  // }

  // const fieldsString = `
  // id
  // name
  // price {
  //   value
  //   currency
  // }
  // category
  // description
  // `;


  //   const query = `
  // {
  //   qQueryEndpoint {
  //     id
  //     name
  //     dateOfBirth
  //   }
  // } 
  // `;

  //   // const mutation = `
  //   // mutation {
  //   //   saveSomething {
  //   //     id
  //   //   }
  //   // }
  //   // `;

  //   const firstOperationDefinition = (ast) => ast.definitions[0];
  //   const firstFieldValueNameFromOperation = (operationDefinition) => operationDefinition.selectionSet.selections[0].name.value;



  //   useEffect(() => {

  //     const mutation = `
  //   mutation SaveSomething(
  //     $id: ID!
  //     $name: String!
  //     $dateOfBirth: String
  //     $orderDetails: [OrderDetailInput!]
  //   ) {
  //     saveSomething(
  //       id: $id
  //       name: $name
  //       dateOfBirth: $dateOfBirth
  //       orderDetails: $orderDetails
  //     ) {
  //       id
  //       name
  //       dateOfBirth
  //       orderDetails {
  //         itemName
  //         qty
  //         description
  //         manufactured {
  //           companyName
  //           year
  //         }
  //       }
  //     }
  //   }
  // `;

  //     function extractVariablesFromMutation(mutationString) {
  //       const variableRegex = /\$(\w+):\s*(\w+)(!)?/g;
  //       const variables = {};
  //       let match;

  //       while ((match = variableRegex.exec(mutationString)) !== null) {
  //         const variableName = match[1];
  //         const variableType = match[2];
  //         const isRequired = match[3] === "!";

  //         variables[variableName] = {
  //           type: variableType,
  //           required: isRequired,
  //         };
  //       }

  //       return variables;
  //     }

  //     // Example usage:
  //     const extractedVariables = extractVariablesFromMutation(mutation);
  //     console.log(JSON.stringify(extractedVariables, null, 2));

  //   }, []);











  // only add edges if interactive is true
  const onConnect = useCallback(
    (connection) => {
      if (interactive && isValidConnection(connection)) {
        // Standard connection logic
        setEdges((eds) => {
          const newEdges = addEdge(
            {
              ...connection,
              type: "simplebezier",
              animated: true,
              style: { strokeWidth: 1.5, stroke: "#475569" },
            },
            eds
          );

          // Check for translateNode connection completion
          const translateNodeId = connection.source.startsWith("data-translation") ? connection.source : connection.target.startsWith("data-translation") ? connection.target : null;

          if (translateNodeId) {
            const connectedEdges = newEdges.filter(edge => edge.source === translateNodeId || edge.target === translateNodeId);
            if (connectedEdges.length === 2) {
              // Delay the execution to allow React Flow to update
              setTimeout(() => {
                const translateNode = getNodes().find(node => node.id === translateNodeId);
                if (translateNode) {
                  const connectedNodesDetails = updateConnectedNodesDetails(translateNode);
                  setSelectedNode(connectedNodesDetails);
                  setShowSidebar(true);
                }
              }, 0); // Minimal delay
            }
          } else {
            // Direct mapping: Sync immediately to ensure persistence
            setTimeout(() => {
              if (reactFlowInstance.current) {
                const allConnections = updateConnections(reactFlowInstance);
                dispatch(updateIntegrationMappingDetails({ key: 'mappingDetails', value: allConnections }));
              }
            }, 0);
          }

          return newEdges;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setEdges, interactive]
  );

  // Use onInit to get the React Flow instance
  const onReactFlowInit = useCallback((_reactFlowInstance) => {
    reactFlowInstance.current = _reactFlowInstance;
  }, []);

  const handleOnDataTranslationClick = useCallback((nodeId) => {
    setTimeout(() => {
      if (reactFlowInstance.current) {
        const allNodes = reactFlowInstance.current.getNodes();
        const clickedNode = allNodes.find((node) => node.id === nodeId);
        if (clickedNode) {
          const connectedNodesDetails = updateConnectedNodesDetails(clickedNode);
          setSelectedNode(connectedNodesDetails);
          setShowSidebar(true);
        } else {
          console.warn(`Node with ID ${nodeId} not found.`);
          setShowSidebar(false);
          setSelectedNode(null);
        }
      }
    }, 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddDataTranslation = useCallback(() => {
    const allNodes = getNodes();
    const existingTranslations = allNodes.filter((n) => n.type === "translateNode");

    // Use root node positions — most reliable reference points
    const sourceRoot = allNodes.find((n) => n.id === "source-request");
    const destRoot = allNodes.find((n) => n.id === "destination-request");

    // Place node at horizontal midpoint between Source root and Destination root
    let midX = 1400; // safe fallback
    if (sourceRoot && destRoot) {
      midX = Math.round((sourceRoot.position.x + destRoot.position.x) / 2);
    } else if (sourceRoot) {
      midX = sourceRoot.position.x + 600;
    }

    // Vertical center: midpoint between top and bottom of all source field nodes
    const sourceFieldNodes = allNodes.filter(
      (n) => n.type === "custom" && n.id.startsWith("source-request")
    );
    let midY = 300;
    if (sourceFieldNodes.length) {
      const ys = sourceFieldNodes.map((n) => n.position.y);
      midY = Math.round((Math.min(...ys) + Math.max(...ys)) / 2);
    }

    // Stack multiple translation nodes vertically with spacing to avoid overlap
    const offsetY = existingTranslations.length * 110;

    const newTranslationNodeId = `data-translation-${getUniqueTranslationId()}`;
    const newTranslationNode = {
      id: newTranslationNodeId,
      type: "translateNode",
      data: {
        label: "Data Translation",
        onClick: handleOnDataTranslationClick,
      },
      position: { x: midX, y: midY + offsetY },
    };
    setNodes((nds) => [...nds, newTranslationNode]);
    toast.success("Data Translation node added", { autoClose: 2000 });
  }, [setNodes, getNodes, handleOnDataTranslationClick]);

  useEffect(() => {
    if (integrationDetails) {
      const safeParse = (str) => {
        if (!str || typeof str !== 'string' || str.trim() === '') return null;
        try {
          return JSON.parse(str);
        } catch (error) {
          console.error("Error parsing schema:", error);
          return null;
        }
      };

      if (integrationDetails.triggerDetails && integrationDetails.triggerDetails.triggerRequestSchema) {
        setTriggerRequestSchema(safeParse(integrationDetails.triggerDetails.triggerRequestSchema));
      } else {
        setTriggerRequestSchema(null);
      }

      if (integrationDetails.sourceDetails && integrationDetails.sourceDetails) {
        setSourceRequestUrl(integrationDetails.sourceDetails.restAPISourceUrl);
      }

      if (integrationDetails.sourceDetails && integrationDetails.sourceDetails.restAPISourceRequestSchema) {
        setSourceRequestSchema(safeParse(integrationDetails.sourceDetails.restAPISourceRequestSchema));
      } else {
        setSourceRequestSchema(null);
      }

      // For AWS S3 and Snowflake: use restAPISourceResponseSchema if provided (and valid),
      // otherwise auto-generate a placeholder schema so the Source column is visible.
      const srcType = integrationDetails.sourceDetails?.sourceType;
      const parsedSourceResponse = safeParse(integrationDetails.sourceDetails?.restAPISourceResponseSchema);
      
      if (parsedSourceResponse) {
        setSourceResponseSchema(parsedSourceResponse);
      } else if (srcType === "AWS S3") {
        // Auto placeholder for AWS S3 — shows a Source column so mappings can be made
        setSourceResponseSchema({
          type: "object",
          properties: {
            key: { type: "string", description: "S3 Object Key (file path)" },
            bucket: { type: "string", description: "S3 Bucket Name" },
            content: { type: "string", description: "File content (string)" },
            content_type: { type: "string", description: "Content-Type header" },
          }
        });
      } else if (srcType === "Snowflake") {
        // Auto placeholder for Snowflake — shows a Source column so mappings can be made
        setSourceResponseSchema({
          type: "object",
          properties: {
            rows: { type: "array", description: "Array of result rows" },
            row_count: { type: "number", description: "Total number of rows returned" },
          }
        });
      } else {
        setSourceResponseSchema(null);
      }

      // For AWS S3 and Snowflake destinations: create placeholder schemas if none provided
      const destType = integrationDetails.destinationDetails?.destinationType;
      const parsedDestinationRequest = safeParse(integrationDetails.destinationDetails?.restAPIDestinationRequestSchema);
      
      if (parsedDestinationRequest) {
        setDestinationRequestSchema(parsedDestinationRequest);
      } else if (destType === "Snowflake") {
        setDestinationRequestSchema({
          type: "object",
          properties: {
            table: { type: "string", description: "Target Snowflake table" },
            record: { type: "object", description: "Row data to insert" },
          }
        });
      } else if (destType === "AWS S3") {
        setDestinationRequestSchema({
          type: "object",
          properties: {
            key: { type: "string", description: "S3 Object Key (file path)" },
            content: { type: "string", description: "File content to upload" },
            content_type: { type: "string", description: "Content-Type" },
          }
        });
      } else {
        setDestinationRequestSchema(null);
      }

      if (integrationDetails.destinationDetails && integrationDetails.destinationDetails.restAPIDestinationResponseSchema) {
        setDestinationResponseSchema(safeParse(integrationDetails.destinationDetails.restAPIDestinationResponseSchema));
      } else {
        setDestinationResponseSchema(null);
      }

      if (integrationDetails.triggerDetails && integrationDetails.triggerDetails.triggerResponseSchema) {
        setTriggerResponseSchema(safeParse(integrationDetails.triggerDetails.triggerResponseSchema));
      } else {
        setTriggerResponseSchema(null);
      }

      if (integrationDetails.mappingDetails) {
        setDataMappings(integrationDetails.mappingDetails);
      }
    }
  }, [integrationDetails]);

  useEffect(() => {
    const newNodes = [];
    const newEdges = [];

    let startX = 10
    // Trigger Root Node
    let triggerRootNode = null;
    let sourceRequestRootNode = null;
    let sourceResponseRootNode = null;
    let destinationRootNode = null;
    let destinationResponseRootNode = null;
    let triggerResponseRootNode = null;


    if (triggerRequestSchema && triggerRequestSchema.properties) {
      triggerRootNode = {
        id: "trigger",
        type: "root",
        data: { label: "Trigger" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false
      };
      newNodes.push(triggerRootNode);
    } else if (triggerRequestSchema && triggerRequestSchema.type === "array") {
      triggerRootNode = {
        id: "trigger",
        type: "root",
        data: { label: "Trigger" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false
      };
      newNodes.push(triggerRootNode);
    }

    // Source Request Root Node
    if ((sourceRequestSchema && (sourceRequestSchema.properties || sourceRequestSchema.type === "array")) || (sourceResponseSchema && (sourceResponseSchema.properties || sourceResponseSchema.type === "array"))) {
      startX += 810;

      sourceRequestRootNode = {
        id: "source-request",
        type: "root",
        data: { label: "Source" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(sourceRequestRootNode);

      // Source Response Root Node
      sourceResponseRootNode = {
        id: "source-response",
        type: "root",
        data: { label: "Source" },
        position: { x: startX, y: nodeYStart },
        sourcePosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(sourceResponseRootNode);
    }

    if (destinationRequestSchema && (destinationRequestSchema.properties || destinationRequestSchema.type === "array")) {
      startX += 1210;
      // Destination Root Node
      destinationRootNode = {
        id: "destination-request",
        type: "root",
        data: { label: "Destination" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        sourcePosition: Position.Right,
        isDraggable: false,
      };
      newNodes.push(destinationRootNode);

      // Destination Root Node
      destinationResponseRootNode = {
        id: "destination-response",
        type: "root",
        data: { label: "Destination" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        sourcePosition: Position.Right,
        isDraggable: false,
      };
      newNodes.push(destinationResponseRootNode);
    }



    // Generate Trigger Request Nodes and Edges
    if (triggerRequestSchema && triggerRequestSchema.properties) {
      const { nodes: newTriggerRequestNodes, edges: newTriggerRequestEdges } =
        createNodesAndEdgesFromSchema(
          triggerRequestSchema,
          "trigger",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          true
        );
      newNodes.push(...newTriggerRequestNodes);
      newEdges.push(...newTriggerRequestEdges);
    } else if (triggerRequestSchema && triggerRequestSchema.type === "array") {
      const { nodes: newTriggerRequestNodes, edges: newTriggerRequestEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(triggerRequestSchema.items) ? triggerRequestSchema.items[0] : triggerRequestSchema.items,
          "trigger",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          true
        );
      newNodes.push(...newTriggerRequestNodes);
      newEdges.push(...newTriggerRequestEdges);
    }

    if (triggerResponseSchema && (triggerResponseSchema.properties || triggerResponseSchema.type === "array")) {
      startX += 810;
      // Destination Root Node
      triggerResponseRootNode = {
        id: "trigger-response",
        type: "root",
        data: { label: "Response" },
        position: { x: startX, y: nodeYStart },
        targetPosition: Position.Bottom,
        isDraggable: false,
      };
      newNodes.push(triggerResponseRootNode);
    }

    // Generate Source Request Nodes and Edges
    if (sourceRequestSchema && sourceRequestSchema.properties) {
      const { nodes: newSourceRequestNodes, edges: newSourceRequestEdges } =
        createNodesAndEdgesFromSchema(
          sourceRequestSchema,
          "source-request",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          true,
          sourceRequestRootNode.position.x
        );
      newNodes.push(...newSourceRequestNodes);
      newEdges.push(...newSourceRequestEdges);
    } else if (sourceRequestSchema && sourceRequestSchema.type === "array") {
      const { nodes: newSourceRequestNodes, edges: newSourceRequestEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(sourceRequestSchema.items) ? sourceRequestSchema.items[0] : sourceRequestSchema.items,
          "source-request",
          100,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          true,
          sourceRequestRootNode.position.x
        );
      newNodes.push(...newSourceRequestNodes);
      newEdges.push(...newSourceRequestEdges);
    } else if (sourceRequestUrl) {
      const urlPathParams = extractPlaceholders(sourceRequestUrl);
      if (urlPathParams && urlPathParams.length > 0) {
        const sourceRequestSchemaDynamic = JSON.parse(getDynamicSchema(urlPathParams));

        const { nodes: newSourceRequestNodes, edges: newSourceRequestEdges } =
          createNodesAndEdgesFromSchema(
            sourceRequestSchemaDynamic,
            "source-request",
            100,
            1,
            90,
            true,
            nodeYIncrement,
            sourceRootNodeX,
            false,
            true,
            sourceRequestRootNode ? sourceRequestRootNode.position.x : sourceResponseRootNode.position.x
          );

        newNodes.push(...newSourceRequestNodes);
        newEdges.push(...newSourceRequestEdges);
      }
    }

    // Generate Source Response Nodes and Edges
    if (sourceResponseSchema && sourceResponseSchema.properties) {
      const { nodes: newSourceResponseNodes, edges: newSourceResponseEdges } =
        createNodesAndEdgesFromSchema(
          sourceResponseSchema,
          "source-response", // Corrected: Parent is now "source"
          sourceRequestNodeXOffset,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          sourceResponseRootNode.position.x
        );

      newNodes.push(...newSourceResponseNodes);
      newEdges.push(...newSourceResponseEdges);
    } else if (sourceResponseSchema && sourceResponseSchema.type === "array") {
      const { nodes: newSourceResponseNodes, edges: newSourceResponseEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(sourceResponseSchema.items) ? sourceResponseSchema.items[0] : sourceResponseSchema.items,
          "source-response", // Corrected: Parent is now "source"
          sourceRequestNodeXOffset,
          1,
          90,
          true,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          sourceResponseRootNode.position.x
        );

      newNodes.push(...newSourceResponseNodes);
      newEdges.push(...newSourceResponseEdges);
    }

    // Generate Destination Nodes and Edges
    if (destinationRequestSchema && destinationRequestSchema.properties) {
      const { nodes: newDestinationNodes, edges: newDestinationEdges } =
        createNodesAndEdgesFromSchema(
          destinationRequestSchema,
          "destination-request",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationRootNode.position.x
        );
      newNodes.push(...newDestinationNodes);
      newEdges.push(...newDestinationEdges);
    } else if (destinationRequestSchema && destinationRequestSchema.type === "array") {
      const { nodes: newDestinationNodes, edges: newDestinationEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(destinationRequestSchema.items) ? destinationRequestSchema.items[0] : destinationRequestSchema.items,
          "destination-request",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationRootNode.position.x
        );
      newNodes.push(...newDestinationNodes);
      newEdges.push(...newDestinationEdges);
    }

    if (destinationResponseSchema && destinationResponseSchema.properties) {
      const { nodes: newDestinationResponseNodes, edges: newDestinationResponseEdges } =
        createNodesAndEdgesFromSchema(
          destinationResponseSchema,
          "destination-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationResponseRootNode.position.x
        );
      newNodes.push(...newDestinationResponseNodes);
      newEdges.push(...newDestinationResponseEdges)
    } else if (destinationResponseSchema && destinationResponseSchema.type === "array") {
      const { nodes: newDestinationResponseNodes, edges: newDestinationResponseEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(destinationResponseSchema.items) ? destinationResponseSchema.items[0] : destinationResponseSchema.items,
          "destination-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          destinationResponseRootNode.position.x
        );
      newNodes.push(...newDestinationResponseNodes);
      newEdges.push(...newDestinationResponseEdges)
    }

    if (triggerResponseSchema && triggerResponseSchema.properties) {
      const { nodes: newTriggerResponseNodes, edges: newTriggerResponseEdges } =
        createNodesAndEdgesFromSchema(
          triggerResponseSchema,
          "trigger-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          triggerResponseRootNode.position.x
        );
      newNodes.push(...newTriggerResponseNodes);
      newEdges.push(...newTriggerResponseEdges)
    } else if (triggerResponseSchema && triggerResponseSchema.type === "array") {
      const { nodes: newTriggerResponseNodes, edges: newTriggerResponseEdges } =
        createNodesAndEdgesFromSchema(
          Array.isArray(triggerResponseSchema.items) ? triggerResponseSchema.items[0] : triggerResponseSchema.items,
          "trigger-response",
          -sourceRequestNodeXOffset,
          1,
          90,
          false,
          nodeYIncrement,
          sourceRootNodeX,
          false,
          false,
          triggerResponseRootNode.position.x
        );
      newNodes.push(...newTriggerResponseNodes);
      newEdges.push(...newTriggerResponseEdges)
    }

    // Load Existing Mappings
    if (dataMappings && dataMappings.length > 0) {
      dataMappings.forEach((mapping, index) => {
        //check if source node and target node already available in nodes
        if (newNodes.find(node => node.id === mapping.source.id) && newNodes.find(node => node.id === mapping.target.id)) {
          // If it's a direct mapping (no translation node)
          if (mapping.isDirect) {
            const sourceNode = newNodes.find((node) => node.id === mapping.source.id);
            const targetNode = newNodes.find((node) => node.id === mapping.target.id);

            if (sourceNode && targetNode) {
              const targetHandle = (targetNode.id.startsWith("source-request") || targetNode.id.startsWith("destination-request")) ? "dt-a" : "dt-b";
              newEdges.push({
                id: mapping.id,
                source: sourceNode.id,
                target: targetNode.id,
                sourceHandle: "dt-a",
                targetHandle: targetHandle,
                type: "simplebezier",
                animated: true,
                style: { strokeWidth: 1.5, stroke: "#475569" },
              });
            }
          } else {
            // Existing Translation Node Logic
            const { source, target, nodeLocation, dataTranslation, id } = mapping;
            const sourceNode = newNodes.find((node) => node.id === source.id);
            const targetNode = newNodes.find((node) => node.id === target.id);

            if (sourceNode && targetNode) {
              const { x, y } = nodeLocation;

              const newTranslationNodeId = id;

              const newTranslationNode = {
                id: newTranslationNodeId,
                type: "translateNode",
                data: {
                  label: "Data Translation",
                  dataTranslation: dataTranslation,
                  onClick: handleOnDataTranslationClick,
                },
                position: { x, y },
              };
              newNodes.push(newTranslationNode);

              // Edge from Source to Data Translation
              newEdges.push({
                id: `e-${sourceNode.id}-${newTranslationNodeId}`,
                source: sourceNode.id,
                sourceHandle: "dt-a",
                target: newTranslationNodeId,
                targetHandle: "dt-b",
                type: "simplebezier",
                animated: true,
                style: { strokeWidth: 1.5, stroke: "#475569" },
              });

              // Edge from Data Translation to Target
              newEdges.push({
                id: `e-${newTranslationNodeId}-${targetNode.id}`,
                source: newTranslationNodeId,
                targetHandle: "dt-a",
                target: targetNode.id,
                type: "simplebezier",
                animated: true,
                style: { strokeWidth: 1.5, stroke: "#475569" },
              });
            }
          }
        }
      });
    }

    setNodes(newNodes);
    setEdges(newEdges);

    if (reactFlowWrapper.current) {
      setTimeout(() => {
        fitView({
          padding: 0.12,
          duration: 500,
          maxZoom: 0.85,
          minZoom: 0.15,
        });
      }, 150);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, fitView, handleAddDataTranslation, dataMappings, triggerRequestSchema, sourceRequestSchema, sourceResponseSchema, destinationRequestSchema, destinationResponseSchema, triggerResponseSchema]);

  // Close sidebar handler
  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setSelectedNode(null);
  };

  const deleteDataTranslation = (node) => {
    if (!reactFlowInstance.current) return;

    const connectedEdges = reactFlowInstance.current.getEdges().filter(
      (e) => e.source === node.id || e.target === node.id
    );

    // Always remove the node and its edges from the canvas directly
    setNodes((nds) => nds.filter((n) => n.id !== node.id));
    setEdges((eds) => eds.filter((e) => e.source !== node.id && e.target !== node.id));

    // Only dispatch to Redux if this node had a saved mapping (was connected)
    // Dispatching rebuilds the entire node graph, which would wipe other unconnected nodes
    if (connectedEdges.length > 0) {
      const allConnections = updateConnections(reactFlowInstance, node).filter(
        (connection) => connection.id !== node.id
      );
      dispatch(updateIntegrationMappingDetails({ key: 'mappingDetails', value: allConnections }));
    }

    handleCloseSidebar();
  }

  const onApplyDataTranslation = () => {
    if (!reactFlowInstance.current) return; // Check if reactFlowInstance is available

    console.log(selectedNode);


    const allConnections = updateConnections(reactFlowInstance, selectedNode);

    dispatch(updateIntegrationMappingDetails({ key: 'mappingDetails', value: allConnections }));


    handleCloseSidebar();
  }

  const updateConnections = (reactFlowInstance, selectedNode = null) => {
    const allConnections = [];

    const translationNodes = reactFlowInstance.current.getNodes().filter(node => node.type === "translateNode");
    translationNodes.forEach(translationNode => {
      const connectedEdges = reactFlowInstance.current.getEdges().filter(edge => edge.source === translationNode.id || edge.target === translationNode.id);

      if (connectedEdges.length === 2) {
        const sourceEdge = connectedEdges.find(edge => edge.target === translationNode.id);
        const targetEdge = connectedEdges.find(edge => edge.source === translationNode.id);

        const sourceNode = reactFlowInstance.current.getNodes().find(node => node.id === sourceEdge.source);
        const destinationNode = reactFlowInstance.current.getNodes().find(node => node.id === targetEdge.target);

        const connectedNodesDetails = {
          id: translationNode.id,
          source: {
            id: sourceNode?.id,
            label: sourceNode?.data?.label || "Root",
            data: sourceNode?.data,
          },
          target: {
            id: destinationNode?.id,
            label: destinationNode?.data?.label || "Root",
            data: destinationNode?.data,
          },
          nodeLocation: {
            x: translationNode.position.x,
            y: translationNode.position.y
          }
        };

        // Merge dataTranslation if source and target IDs match
        if (selectedNode &&
          selectedNode.source.id === sourceNode.id &&
          selectedNode.target.id === destinationNode.id) {
          connectedNodesDetails.dataTranslation = selectedNode.dataTranslation;
        } else if (translationNode && translationNode.data && translationNode.data.dataTranslation) {
          connectedNodesDetails.dataTranslation = translationNode.data.dataTranslation;
        }

        allConnections.push(connectedNodesDetails);
      } else {
        allConnections.push(
          {
            id: translationNode.id,
            source: {
              id: null,
              label: "Root",
              data: null,
            },
            target: {
              id: null,
              label: "Root",
              data: null,
            },
            nodeLocation: {
              x: translationNode.position.x,
              y: translationNode.position.y
            },
            dataTranslation: {}
          }
        );
      }

    });

    // Capture Direct Mappings
    const edges = reactFlowInstance.current.getEdges();
    const directEdges = edges.filter(edge =>
      !edge.source.startsWith("data-translation") &&
      !edge.target.startsWith("data-translation") &&
      // Filter out structural edges
      !edge.id.startsWith("e-trigger-") &&
      !edge.id.startsWith("e-source-request-") &&
      !edge.id.startsWith("e-source-response-") &&
      !edge.id.startsWith("e-destination-request-") &&
      !edge.id.startsWith("e-destination-response-") &&
      !edge.id.startsWith("e-trigger-response-") &&
      !edge.source.endsWith("-response") && // Extra safety
      (edge.source.startsWith("trigger") || edge.source.startsWith("source-response") || edge.source.startsWith("destination-response") || edge.source.startsWith("destination-request")) // Ensure it comes from a valid source
    );

    directEdges.forEach(edge => {
      const sourceNode = reactFlowInstance.current.getNodes().find(node => node.id === edge.source);
      const destinationNode = reactFlowInstance.current.getNodes().find(node => node.id === edge.target);

      if (sourceNode && destinationNode) {
        const connectedNodesDetails = {
          id: edge.id, // Use edge ID as the unique ID for this mapping
          source: {
            id: sourceNode.id,
            label: sourceNode.data.label,
            data: sourceNode.data,
          },
          target: {
            id: destinationNode.id,
            label: destinationNode.data.label,
            data: destinationNode.data,
          },
          nodeLocation: { x: 0, y: 0 }, // No specific location node
          dataTranslation: {}, // Empty translation for direct mapping
          isDirect: true // Flag to identify direct mapping
        };
        allConnections.push(connectedNodesDetails);
      }
    });

    return allConnections;
  }

  const updateConnectedNodesDetails = (clickedNode) => {
    console.log("updateConnectedNodesDetails clickedNode:", clickedNode);
    const connectedEdges = getEdges().filter(
      (edge) => edge.source === clickedNode.id || edge.target === clickedNode.id
    );

    console.log("updateConnectedNodesDetails connectedEdges:", connectedEdges);

    let sourceNode = null;
    let destinationNode = null;

    if (clickedNode.type === "translateNode") {
      // Find the edge where the translateNode is the target (incoming edge)
      const incomingEdge = connectedEdges.find((edge) => edge.target === clickedNode.id);
      // Find the edge where the translateNode is the source (outgoing edge)
      const outgoingEdge = connectedEdges.find((edge) => edge.source === clickedNode.id);

      if (incomingEdge) {
        sourceNode = getNodes().find((node) => node.id === incomingEdge.source);
      }
      if (outgoingEdge) {
        destinationNode = getNodes().find((node) => node.id === outgoingEdge.target);
      }
    } else {
      const edge = connectedEdges[0];
      if (edge) {
        if (edge.source === clickedNode.id) {
          destinationNode = getNodes().find((node) => node.id === edge.target);
        } else if (edge.target === clickedNode.id) {
          sourceNode = getNodes().find((node) => node.id === edge.source);
        }
      }
    }

    console.log("updateConnectedNodesDetails sourceNode:", sourceNode);
    console.log("updateConnectedNodesDetails destinationNode:", destinationNode);

    const connectedNodesDetails = {
      id: clickedNode.id,
      source: {
        id: sourceNode?.id,
        label: sourceNode?.data?.label || "Root",
        data: sourceNode?.data,
      },
      target: {
        id: destinationNode?.id,
        label: destinationNode?.data?.label || "Root",
        data: destinationNode?.data,
      },
      dataTranslation: clickedNode.data.dataTranslation,
    };
    console.log(
      "updateConnectedNodesDetails connectedNodesDetails:",
      connectedNodesDetails
    );
    return connectedNodesDetails;
  };


  return (
    <div className="relative h-[calc(100vh-190px)]" ref={reactFlowWrapper}>
      {/* Sidebar */}
      {showSidebar && (
        <div className="absolute right-0 top-0 z-10 h-full w-[33%] overflow-auto rounded-md border border-blue-400 bg-theme-primary p-4">
          <DataTranslationForm selectedNode={selectedNode} />
          <div className="absolute bottom-0 right-4 flex gap-2">
            <button
              onClick={handleCloseSidebar}
              className="mb-4 rounded-md bg-gray-500 p-2 text-gray-900 hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteDataTranslation(selectedNode)}
              className="mb-4 rounded-md bg-red-400 p-2 text-gray-900 hover:bg-red-500"
            >
              Delete
            </button>
            <button
              onClick={onApplyDataTranslation}
              className="mb-4 rounded-md bg-blue-400 p-2 text-gray-900 hover:bg-blue-600"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Edge hover tooltip */}
      {edgeTooltip && (
        <div
          style={{
            position: "fixed",
            left: edgeTooltip.x + 12,
            top: edgeTooltip.y - 34,
            zIndex: 9999,
            pointerEvents: "none",
          }}
          className="rounded bg-gray-800 px-2 py-1 text-xs text-white shadow-lg whitespace-nowrap"
        >
          Double-click to delete
        </div>
      )}

      {/* Fixed "Add Data Translation" Button */}
      {interactive && (
        <div className="absolute left-4 top-4 z-20">
          <DataTranslationButtonNode
            data={{
              label: "Add Data Translation",
              onClick: handleAddDataTranslation,
            }}
          />
        </div>
      )}



      <ReactFlow
        className="inner-strong-shadow rounded-md"
        nodeTypes={nodeTypes}
        style={{ width: "100%", height: "100%", backgroundColor: "#ffffff" }}
        nodes={nodes}
        edges={edges}
        elementsSelectable={interactive}
        nodesDraggable={interactive}
        nodesConnectable={interactive}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        connectionLineType="simplebezier"
        connectionLineStyle={{ strokeWidth: 1.5, stroke: "#475569" }}
        isValidConnection={isValidConnection}
        defaultEdgeOptions={{
          type: "simplebezier",
          style: { strokeWidth: 1.5, stroke: "#475569" },
        }}
        onInit={onReactFlowInit}
        onEdgeDoubleClick={interactive ? (e, edge) => {
          e.stopPropagation();
          setEdgeTooltip(null);
          setEdges((eds) => eds.filter((ed) => ed.id !== edge.id));
        } : undefined}
        onEdgeClick={interactive ? () => setEdgeTooltip(null) : undefined}
        onEdgeMouseEnter={interactive ? (e) => {
          setEdgeTooltip({ x: e.clientX, y: e.clientY });
        } : undefined}
        onEdgeMouseLeave={interactive ? () => setEdgeTooltip(null) : undefined}
      >
        <Controls />
        <Background color="#cbd5e1" variant="dots" />
      </ReactFlow>
    </div>
  );
};

export default DataMappingDiagram;
